'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { Pill } from '@/components/ui/pill';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';

export function AboutFeaturesSection() {
  const t = useTranslations('aboutPage.features');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  const features = [
    t('feature1'),
    t('feature2'),
    t('feature3'),
    t('feature4'),
    t('feature5'),
    t('feature6'),
    t('feature7'),
    t('feature8'),
  ].filter(Boolean); // Filter out any undefined values

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="default"
      padding="md"
      className="relative overflow-hidden bg-white dark:bg-gray-950"
    >
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="container relative z-10 max-w-7xl mx-auto">
        <div className={cn(
          "mb-12 text-center transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          {t('pill') && (
            <div className="mb-4 flex justify-center">
              <Pill variant="default" className="text-gray-900 dark:text-white">
                {t('pill')}
              </Pill>
            </div>
          )}
          <SectionHeading
            page=""
            align="center"
            fallbackTitle={t('title')}
            fallbackSubtitle={t('subtitle')}
          />
        </div>

        {/* 2x4 Grid of Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={cn(
                "bg-gray-100 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:border-primary-500/50 hover:shadow-lg",
                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              )}
              style={{ transitionDelay: `${200 + index * 50}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-white" />
                </div>
                <Typography variant="p" className="text-gray-100 dark:text-gray-200 leading-relaxed text-sm md:text-base">
                  {feature}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
}

